import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import WebFont from "webfontloader";
import { useAuth } from "../auth/AuthContext";
import BadgeEditor from "../components/BadgeEditor";
import Header from "../components/Header";
import { getBadgeTemplateTextFonts } from "../helpers/badgeTemplate";
import { isPaymentsFeatureEnabled } from "../payments/paymentFeature";
import {
  clearPendingGeneration,
  confirmBadgePurchase,
  hasPaidBadgeSets,
  readPendingGeneration,
} from "../payments/paymentsService";
import { EventEditorPageSkeleton } from "./EventPageSkeletons";
import { getEvent, getEventForAdmin, updateEvent } from "./eventsService";
import "./Events.css";

const clearPaymentReturnQuery = () => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete("payment");
  searchParams.delete("session_id");
  const nextSearch = searchParams.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`
  );
};

const EventEditorPage = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const { isAdmin, session, user } = useAuth();
  const isReadOnly = isAdmin;
  const createdEvent = location.state?.event?.id === eventId ? location.state.event : null;
  const [paymentsEnabled] = useState(isPaymentsFeatureEnabled);
  const paymentReturn = new URLSearchParams(location.search).get("payment");
  const checkoutSessionId =
    new URLSearchParams(location.search).get("session_id") || "";
  const isReturningFromPayment =
    paymentsEnabled && paymentReturn === "success" && Boolean(checkoutSessionId);
  const [event, setEvent] = useState(() => createdEvent);
  const [error, setError] = useState("");
  const [isEventLoading, setIsEventLoading] = useState(!createdEvent);
  const [isPaymentStatusLoading, setIsPaymentStatusLoading] = useState(
    paymentsEnabled && !isReturningFromPayment
  );
  const [hasPurchasedBadgeSets, setHasPurchasedBadgeSets] = useState(false);
  const [isPaymentConfirmationLoading, setIsPaymentConfirmationLoading] =
    useState(isReturningFromPayment);
  const [confirmedPurchase, setConfirmedPurchase] = useState(null);
  const [areFontsLoaded, setAreFontsLoaded] = useState(Boolean(createdEvent));
  const saveTimerRef = useRef(null);
  const pendingSavePayloadRef = useRef(null);
  const eventNameRef = useRef(event?.name);
  const isUnmountingRef = useRef(false);
  const saveRequestIdRef = useRef(0);
  const badgeTemplate = event?.badgeTemplate;
  const loadedEventId = event?.id;
  const templateTextFontFamilies =
    getBadgeTemplateTextFonts(badgeTemplate).join("\n");

  useEffect(() => {
    eventNameRef.current = event?.name;
  }, [event?.name]);

  useEffect(() => {
    if (!loadedEventId) {
      return undefined;
    }

    let isMounted = true;
    let fontLoadTimeout = null;
    const finishFontLoading = () => {
      if (isMounted) {
        setAreFontsLoaded(true);
      }

      if (fontLoadTimeout) {
        clearTimeout(fontLoadTimeout);
      }
    };

    const templateFonts = templateTextFontFamilies
      ? templateTextFontFamilies.split("\n")
      : [];
    if (!templateFonts.length) {
      finishFontLoading();
      return () => {
        isMounted = false;
      };
    }

    fontLoadTimeout = setTimeout(finishFontLoading, 5000);

    try {
      WebFont.load({
        google: {
          families: templateFonts,
        },
        active: finishFontLoading,
        inactive: finishFontLoading,
      });
    } catch {
      finishFontLoading();
    }

    return () => {
      isMounted = false;
      clearTimeout(fontLoadTimeout);
    };
  }, [loadedEventId, templateTextFontFamilies]);

  useEffect(() => {
    if (createdEvent) {
      setEvent((currentEvent) =>
        currentEvent?.id === createdEvent.id ? currentEvent : createdEvent
      );
      setIsEventLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadEvent = async () => {
      setIsEventLoading(true);
      setError("");

      try {
        const loadedEvent = isAdmin
          ? await getEventForAdmin(eventId)
          : await getEvent(eventId, user.id);
        if (isMounted) {
          setEvent(loadedEvent);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || "Could not load event.");
        }
      } finally {
        if (isMounted) {
          setIsEventLoading(false);
        }
      }
    };

    loadEvent();

    return () => {
      isMounted = false;
    };
  }, [createdEvent, eventId, isAdmin, user.id]);

  useEffect(() => {
    let isMounted = true;

    if (isReadOnly || !paymentsEnabled || isReturningFromPayment) {
      setHasPurchasedBadgeSets(false);
      setIsPaymentStatusLoading(false);
      return undefined;
    }

    setIsPaymentStatusLoading(true);
    hasPaidBadgeSets(eventId, user.id)
      .then((hasPayments) => {
        if (isMounted) setHasPurchasedBadgeSets(hasPayments);
      })
      .catch(() => {
        if (isMounted) setHasPurchasedBadgeSets(false);
      })
      .finally(() => {
        if (isMounted) setIsPaymentStatusLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [eventId, isReadOnly, isReturningFromPayment, paymentsEnabled, user.id]);

  useEffect(() => {
    if (paymentsEnabled && paymentReturn === "cancelled") {
      clearPaymentReturnQuery();
      toast.info("Payment was cancelled. No badges were generated.");
    }
  }, [paymentReturn, paymentsEnabled]);

  useEffect(() => {
    if (!isReturningFromPayment || !session?.access_token) {
      if (!isReturningFromPayment) setIsPaymentConfirmationLoading(false);
      return undefined;
    }

    let isMounted = true;
    const savedGeneration = readPendingGeneration(checkoutSessionId);

    setIsPaymentConfirmationLoading(true);
    confirmBadgePurchase({ sessionId: checkoutSessionId })
      .then(({ payment }) => {
        if (!savedGeneration?.format || !payment) {
          throw new Error("The paid badge generation details could not be restored.");
        }
        if (!isMounted) return;

        setConfirmedPurchase({ payment, savedGeneration });
        setHasPurchasedBadgeSets(true);
        clearPendingGeneration(checkoutSessionId);
        clearPaymentReturnQuery();
      })
      .catch((confirmationError) => {
        if (isMounted) {
          setError(
            confirmationError.message || "Could not verify the payment."
          );
          clearPaymentReturnQuery();
        }
      })
      .finally(() => {
        if (isMounted) setIsPaymentConfirmationLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    checkoutSessionId,
    isReturningFromPayment,
    session?.access_token,
  ]);

  useEffect(() => () => {
    isUnmountingRef.current = true;
    clearTimeout(saveTimerRef.current);
    const pendingPayload = pendingSavePayloadRef.current;
    pendingSavePayloadRef.current = null;

    if (pendingPayload && !isReadOnly) {
      updateEvent(eventId, user.id, {
        name: eventNameRef.current,
        ...pendingPayload,
      }).catch((saveError) => {
        console.error("Could not finish saving event in background.", saveError);
      });
    }
  }, [eventId, isReadOnly, user.id]);

  const handleEventChange = useMemo(() => {
    return (payload) => {
      if (isReadOnly) return;
      if (isUnmountingRef.current) {
        updateEvent(eventId, user.id, {
          name: eventNameRef.current,
          ...payload,
        }).catch((saveError) => {
          console.error("Could not finish saving event in background.", saveError);
        });
        return;
      }

      pendingSavePayloadRef.current = payload;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        const payloadToSave = pendingSavePayloadRef.current;
        pendingSavePayloadRef.current = null;
        saveTimerRef.current = null;

        if (!payloadToSave) {
          return;
        }

        const saveRequestId = saveRequestIdRef.current + 1;
        saveRequestIdRef.current = saveRequestId;

        try {
          const updatedEvent = await updateEvent(eventId, user.id, {
            name: eventNameRef.current,
            ...payloadToSave,
          });
          if (saveRequestIdRef.current === saveRequestId && !isUnmountingRef.current) {
            setEvent(updatedEvent);
          }
        } catch (saveError) {
          if (saveRequestIdRef.current === saveRequestId && !isUnmountingRef.current) {
            setError(saveError.message || "Could not save event changes.");
          }
        }
      }, 900);
    };
  }, [eventId, isReadOnly, user.id]);

  if (
    isEventLoading ||
    isPaymentStatusLoading ||
    isPaymentConfirmationLoading ||
    (!createdEvent && event && !areFontsLoaded)
  ) {
    return (
      <EventEditorPageSkeleton
        headerProps={{ backTo: isAdmin ? "/admin" : "/events", backLabel: isAdmin ? "Back to admin" : "Back to events" }}
      />
    );
  }

  if (error && !event) {
    return (
      <>
        <Header backTo={isAdmin ? "/admin" : "/events"} backLabel={isAdmin ? "Back to admin" : "Back to events"} />
        <main className="events-shell"><div className="event-error" role="alert">{error}</div></main>
      </>
    );
  }

  return (
    <>
      {error && <div className="event-editor-status">{error}</div>}
      <BadgeEditor
        key={event.id}
        mode="event"
        eventData={event}
        initialConfirmedPurchase={confirmedPurchase}
        initialHasPurchasedBadgeSets={hasPurchasedBadgeSets}
        headerProps={{
          backTo: isAdmin ? "/admin" : "/events",
          backLabel: isAdmin ? "Back to admin" : "Back to events",
          title: event.name,
        }}
        onEventChange={handleEventChange}
        readOnly={isReadOnly}
      />
    </>
  );
};

export default EventEditorPage;
